import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowTopRightOnSquareIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { fetchPatientCard } from '../../../../services/patientCard';
import { useToast } from '../../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../../utils/apiError';
import { copyToClipboard } from '../../../../utils/clipboard';
import {
    createSparkCase,
    linkSparkCase,
    unlinkSparkCase,
    searchSparkCases,
    type SparkCaseMatch
} from '../../../../services/sparkSubmission';
import { CARD_STALE_MS, type RegisterFooter } from './spark.shared';

//  Step 1 — select an existing Spark case to link, or create a new one. The
//  link/create is applied by the footer's primary button (select, then Continue).

export function CreateCaseStep({
    patientId,
    card,
    caseId,
    link,
    isNew,
    onReady,
    onUnlinked,
    registerFooter
}: {
    patientId: string;
    card: Awaited<ReturnType<typeof fetchPatientCard>> | undefined;
    caseId: string | null;
    link: string | null;
    isNew: boolean;
    onReady: (caseId: string, link: string, isNew?: boolean) => void;
    onUnlinked: () => void;
    registerFooter: RegisterFooter;
}) {
    const { showToast } = useToast();
    const alreadyLinked = Boolean(caseId);

    const search = useQuery({
        queryKey: ['spark-search', patientId],
        queryFn: () => searchSparkCases(patientId),
        enabled: !alreadyLinked,
        staleTime: CARD_STALE_MS
    });

    // null = idle, 'create' = creating, number = linking that caseId
    const [busy, setBusy] = useState<null | 'create' | number>(null);
    const [unlinking, setUnlinking] = useState(false);
    // The case the user has selected to link (not yet committed).
    const [selectedCaseId, setSelectedCaseId] = useState<number | null>(null);

    const handleUnlink = useCallback(async () => {
        setUnlinking(true);
        try {
            await unlinkSparkCase(patientId);
            showToast('Case unlinked.', 'success');
            // Clear any stale selection so the step returns to a fresh create/link choice.
            setSelectedCaseId(null);
            onUnlinked();
        } catch (err) {
            showToast(apiErrorMessage(err, 'Failed to unlink the Spark case.'), 'error');
        } finally {
            // The step stays mounted (still on the Case step), so reset the flag or the
            // footer's Continue/Create button stays disabled.
            setUnlinking(false);
        }
    }, [patientId, onUnlinked, showToast]);

    const handleCreate = useCallback(async () => {
        setBusy('create');
        try {
            const res = await createSparkCase(patientId);
            showToast('Spark case created.', 'success');
            onReady(res.caseId, res.link, true);
        } catch (err) {
            showToast(apiErrorMessage(err, 'Failed to create the Spark case.'), 'error');
            setBusy(null);
        }
    }, [patientId, onReady, showToast]);

    const handleLink = useCallback(
        async (matchCaseId: number) => {
            setBusy(matchCaseId);
            try {
                const res = await linkSparkCase(patientId, matchCaseId);
                showToast(`Linked Spark case #${matchCaseId}.`, 'success');
                onReady(res.caseId, res.link, false);
            } catch (err) {
                showToast(apiErrorMessage(err, 'Failed to link the Spark case.'), 'error');
                setBusy(null);
            }
        },
        [patientId, onReady, showToast]
    );

    const fullName = card
        ? `${card.firstName ?? ''} ${card.lastName ?? ''}`.trim() || card.name
        : '';
    const dob = card?.dateOfBirth ? card.dateOfBirth.slice(0, 10) : '—';
    const gender = card?.gender ?? '—';
    const sparkAcct = card?.practice?.sparkAcct ?? null;
    const matches = search.data?.matches ?? [];

    // Footer primary action: Continue (advance) when linked; Continue (link the
    // selected case) when one is selected; otherwise create a new case.
    const onPrimary = useCallback(() => {
        if (alreadyLinked) {
            onReady(caseId!, link ?? '', isNew);
            return;
        }
        if (selectedCaseId != null) {
            void handleLink(selectedCaseId);
            return;
        }
        void handleCreate();
    }, [alreadyLinked, caseId, link, isNew, onReady, selectedCaseId, handleLink, handleCreate]);

    const primaryLabel = alreadyLinked
        ? 'Continue'
        : selectedCaseId != null
          ? busy === selectedCaseId
              ? 'Linking…'
              : 'Continue'
          : busy === 'create'
            ? 'Creating…'
            : matches.length > 0
              ? 'Create new case & continue'
              : 'Create case & continue';

    const primaryDisabled = busy !== null || unlinking;

    useEffect(() => {
        registerFooter({ label: primaryLabel, onClick: onPrimary, disabled: primaryDisabled });
        return () => registerFooter(null);
    }, [registerFooter, primaryLabel, onPrimary, primaryDisabled]);

    return (
        <div className="w-full max-w-2xl mx-auto flex flex-col gap-4">
            {/* Patient identity */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-3">
                    Patient identity (sent to Spark)
                </p>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    <Field label="Name" value={fullName} />
                    <Field label="Date of birth" value={dob} />
                    <Field label="Gender" value={gender} />
                    <Field label="Spark account" value={sparkAcct ?? '—'} />
                </dl>
            </div>

            {alreadyLinked ? (
                <div className="bg-green-50 border border-green-200 rounded-xl p-6 flex flex-col gap-3">
                    <div className="flex items-center gap-2 text-green-700 font-semibold text-sm">
                        <CheckIcon className="w-4 h-4" />{' '}
                        {isNew ? 'New case created' : 'Case linked'}
                    </div>
                    <div className="flex items-center justify-between gap-3">
                        <span className="text-xs text-gray-600">
                            Case ID: <span className="font-mono text-gray-900">{caseId}</span>
                        </span>
                        <CopyButton value={caseId!} label="Copy ID" />
                    </div>
                    {link && (
                        <div className="flex items-center justify-between gap-3 min-w-0">
                            <a
                                href={link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 min-w-0 text-xs text-[#3773A8] font-medium hover:underline"
                            >
                                <span className="truncate">{link}</span>
                                <ArrowTopRightOnSquareIcon className="w-3 h-3 flex-shrink-0" />
                            </a>
                            <CopyButton value={link} label="Copy link" />
                        </div>
                    )}
                    <div className="flex items-center justify-between gap-4">
                        <p className="text-[11px] text-gray-400 flex-1">
                            Wrong case? Unlinking clears the stored case ID + link so you can search
                            or create again. It does not delete the case in Spark.
                        </p>
                        <button
                            type="button"
                            onClick={handleUnlink}
                            disabled={unlinking}
                            className="flex-shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-lg border border-red-300 text-red-700 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <XMarkIcon className="w-4 h-4" />
                            {unlinking ? 'Unlinking…' : 'Unlink case'}
                        </button>
                    </div>
                </div>
            ) : search.isLoading ? (
                <div className="text-sm text-gray-400 py-6 text-center">
                    Checking Spark for an existing case…
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    {matches.length > 0 && (
                        <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-3">
                            <p className="text-xs font-semibold text-gray-700">
                                Found {matches.length} existing case
                                {matches.length === 1 ? '' : 's'} in Spark — select one to link
                            </p>
                            {matches.map((m) => (
                                <MatchRow
                                    key={m.caseId}
                                    match={m}
                                    selected={selectedCaseId === m.caseId}
                                    disabled={busy !== null}
                                    onSelect={() =>
                                        setSelectedCaseId((cur) =>
                                            cur === m.caseId ? null : m.caseId
                                        )
                                    }
                                />
                            ))}
                            <p className="text-[11px] text-gray-400">
                                Select a case and press Continue to link it (skips creating a
                                duplicate), or create a new case below.
                            </p>
                        </div>
                    )}

                    {search.isError && (
                        <p className="text-xs text-amber-600">
                            Couldn't search Spark for existing cases — you can still create a new
                            one.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}

function MatchRow({
    match,
    selected,
    disabled,
    onSelect
}: {
    match: SparkCaseMatch;
    selected: boolean;
    disabled: boolean;
    onSelect: () => void;
}) {
    const name = `${match.firstName ?? ''} ${match.lastName ?? ''}`.trim() || '—';
    return (
        <button
            type="button"
            onClick={onSelect}
            disabled={disabled}
            aria-pressed={selected}
            className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                selected
                    ? 'border-gray-800 bg-gray-50 ring-1 ring-gray-800'
                    : 'border-gray-200 hover:border-gray-300'
            }`}
        >
            <div className="flex flex-col min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="font-mono text-xs text-gray-900">#{match.caseId}</span>
                    <span className="inline-flex items-center px-2 py-[1px] rounded-full text-[10px] font-medium bg-blue-100 text-blue-800">
                        {match.status}
                    </span>
                </div>
                <span className="text-xs text-gray-600 truncate">{name}</span>
            </div>
            <span
                className={`flex-shrink-0 flex items-center justify-center w-5 h-5 rounded-full border-2 ${
                    selected ? 'border-gray-800 bg-gray-800 text-white' : 'border-gray-300'
                }`}
            >
                {selected && <CheckIcon className="w-3 h-3" />}
            </span>
        </button>
    );
}

function Field({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex flex-col">
            <dt className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                {label}
            </dt>
            <dd className="text-sm text-gray-800">{value || '—'}</dd>
        </div>
    );
}

//  Copy-to-clipboard pill (mirrors the manual task's CopyButton)

function CopyButton({ value, label = 'Copy' }: { value: string; label?: string }) {
    const [copied, setCopied] = useState(false);
    const onClick = async (e: React.MouseEvent): Promise<void> => {
        e.stopPropagation();
        if (!(await copyToClipboard(value))) return;
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
    };
    const base =
        'inline-flex items-center gap-1 px-2 py-[3px] rounded-md border text-[11px] font-medium transition-colors whitespace-nowrap flex-shrink-0';
    const state = copied
        ? 'border-green-500 text-green-700 bg-green-50'
        : 'border-gray-300 text-gray-500 bg-white hover:border-blue-500 hover:text-blue-600';
    return (
        <button type="button" onClick={onClick} className={`${base} ${state}`}>
            {copied ? (
                <>
                    <CheckIcon className="w-3 h-3" /> Copied
                </>
            ) : (
                label
            )}
        </button>
    );
}
